/**
 * Speaking Player — intro Page + record-audio prompts (layout: stacked or split, display: all or tabs)
 */

import * as React from 'react';
import {
    cn,
    Button,
    FeedbackCard,
    useIsMobile,
    RichTextViewer,
    isTiptapDocument,
} from '@ai-content-hub/content-kit';
import {
    isCorrectAnswerRevealed,
    useLocale,
    type GradingResult,
    CompositePlayerGroupedItemsBody,
    orderItemsByQuestionsSidebarRows,
    useCompositePlayerSidebarLayout,
    ContentPlayerSplitLayout,
    QuestionCounterLabel,
} from '@ai-content-hub/content-core';
import {
    PagePlayer,
    RecordAudioPlayer,
    defaultPageData,
    hasVisiblePageContent,
    imageMaxHeightVars,
    SPLIT_COLUMN_IMAGE_MAX_HEIGHT,
    type PageElementData,
} from '@ai-content-hub/content-elements';
import type { RecordAudioAnswer } from '@ai-content-hub/content-elements';
import type { SpeakingData, SpeakingItem, SpeakingConfig, SpeakingLayoutConfig } from './schema';
import { defaultSpeakingData } from './schema';
import { SpeakingAiGradePanel } from './SpeakingAiGradePanel';
import { SpeakingPronunciationPanel } from './SpeakingPronunciationPanel';
import { Volume2 } from 'lucide-react';
import map from 'lodash/map';

export interface SpeakingPlayerProps {
    data?: SpeakingData;
    value?: SpeakingData;
    config?: SpeakingConfig;
    answer?: Record<string, RecordAudioAnswer>;
    onAnswer?: (answer: Record<string, RecordAudioAnswer>) => void;
    isReview?: boolean;
    disabled?: boolean;
    showFeedback?: boolean;
    className?: string;
    /** Populated by ContentPlayer after submit when AI grading runs */
    result?: GradingResult;
}

function isPromptRecorded(raw: RecordAudioAnswer | undefined): boolean {
    return typeof raw === 'string' && raw.trim() !== '';
}

function shallowAnswerMapsEqual(
    a: Record<string, RecordAudioAnswer>,
    b: Record<string, RecordAudioAnswer>
): boolean {
    if (a === b) {
        return true;
    }
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) {
        return false;
    }
    for (const k of keysA) {
        if (a[k] !== b[k]) {
            return false;
        }
    }
    return true;
}

function getSpeakingTabButtonProps(options: {
    isCurrent: boolean;
    answered: boolean;
    visited: boolean;
}): { variant: 'default' | 'outline'; className: string } {
    const { isCurrent, answered, visited } = options;
    const base = 'speaking-player-tab cursor-pointer min-w-9 transition-colors duration-200';

    if (isCurrent) {
        return {
            variant: 'default',
            className: cn(
                base,
                'speaking-player-tab--current ring-2 ring-primary/40 ring-offset-2 ring-offset-background'
            ),
        };
    }
    if (answered) {
        return {
            variant: 'outline',
            className: cn(
                base,
                'speaking-player-tab--answered !border-success/60 !bg-success/15 text-foreground shadow-none hover:!bg-success/25 hover:text-foreground dark:!bg-success/15 dark:hover:!bg-success/25'
            ),
        };
    }
    if (visited) {
        return {
            variant: 'outline',
            className: cn(
                base,
                'speaking-player-tab--visited-unanswered !border-warning/60 !bg-warning/15 text-foreground shadow-none hover:!bg-warning/25 hover:text-foreground dark:!bg-warning/15 dark:hover:!bg-warning/25'
            ),
        };
    }
    return {
        variant: 'outline',
        className: cn(
            base,
            'speaking-player-tab--unseen border-border text-muted-foreground shadow-none hover:!bg-muted/40'
        ),
    };
}

export const SpeakingPlayer: React.FC<SpeakingPlayerProps> = ({
    data: dataProp,
    value: valueProp,
    answer: answerProp = {},
    onAnswer,
    config,
    isReview = false,
    disabled = false,
    showFeedback: showFeedbackProp = false,
    className,
    result,
}) => {
    const data = dataProp ?? valueProp;
    const i18nConfig = config?.i18n;
    const { t } = useLocale({ config: i18nConfig });
    // ELMS-2370: LMS "hide answer" => behavior.showCorrectAnswer === false.
    const revealCorrect = isCorrectAnswerRevealed(config);
    const isMobilePlayer = useIsMobile(768);

    const safeData: SpeakingData = {
        ...defaultSpeakingData,
        ...data,
        pageContent: {
            ...defaultPageData,
            ...(data?.pageContent ?? {}),
            sections: data?.pageContent?.sections ?? defaultPageData.sections,
        },
        items: data?.items ?? [],
    };

    const hasRootDescription = Boolean(safeData.description?.trim());

    const [answers, setAnswers] = React.useState<Record<string, RecordAudioAnswer>>(() => ({
        ...answerProp,
    }));

    React.useEffect(() => {
        setAnswers((prev) => (shallowAnswerMapsEqual(prev, answerProp) ? prev : { ...answerProp }));
    }, [answerProp]);

    const handlePromptAnswer = React.useCallback(
        (itemId: string, value: RecordAudioAnswer) => {
            if (disabled) return;
            setAnswers((prev) => {
                const next = { ...prev, [itemId]: value };
                onAnswer?.(next);
                return next;
            });
        },
        [disabled, onAnswer]
    );

    const items = safeData.items;
    const itemIds = React.useMemo(() => map(items, (it) => it.id), [items]);
    const itemById = React.useMemo(
        () => new Map(map(items, (it) => [it.id, it] as const)),
        [items]
    );
    const { sidebarRows } = useCompositePlayerSidebarLayout(itemIds, safeData.questionsSidebarRows);
    const orderedItems = React.useMemo(
        () => orderItemsByQuestionsSidebarRows(items, sidebarRows),
        [items, sidebarRows]
    );
    const orderedItemIndexById = React.useMemo(
        () => new Map(map(orderedItems, (it, idx) => [it.id, idx] as const)),
        [orderedItems]
    );
    const layoutCfg = config?.layout as SpeakingLayoutConfig | undefined;
    const layoutType = layoutCfg?.type;
    const displayModeRaw = layoutCfg?.displayMode ?? 'all';
    const displayMode = displayModeRaw === 'step' ? 'tabs' : displayModeRaw;
    const splitRatio = layoutCfg?.splitRatio;
    const submittedOrFeedback = Boolean(showFeedbackProp || isReview);

    const pageContent = React.useMemo((): PageElementData => {
        return {
            ...defaultPageData,
            ...safeData.pageContent,
            sections: safeData.pageContent?.sections ?? defaultPageData.sections,
        };
    }, [safeData.pageContent]);

    // EAA-2063: hidden blocks (audio script, rubric hints for the grader) do not count.
    const hasPageContent = hasVisiblePageContent(pageContent);
    const panelHorizontal =
        hasPageContent &&
        (layoutType === 'horizontal' || (layoutType === 'auto' && !isMobilePlayer));

    const [stepIndex, setStepIndex] = React.useState(0);
    const [visitedTabIndices, setVisitedTabIndices] = React.useState<Set<number>>(() => new Set());

    React.useEffect(() => {
        if (orderedItems.length === 0) {
            setStepIndex(0);
            return;
        }
        setStepIndex((i) => Math.min(Math.max(0, i), orderedItems.length - 1));
    }, [orderedItems.length]);

    React.useEffect(() => {
        if (orderedItems.length === 0) {
            return;
        }
        setVisitedTabIndices((prev) => {
            const next = new Set<number>();
            prev.forEach((i) => {
                if (i >= 0 && i < orderedItems.length) {
                    next.add(i);
                }
            });
            next.add(stepIndex);
            return next;
        });
    }, [stepIndex, orderedItems.length]);

    const showCounter = items.length > 1;

    const renderPromptCard = (item: SpeakingItem, index: number) => (
        <div className="speaking-player-prompt" key={item.id}>
            <div
                className={cn(
                    'speaking-player-prompt-inner',
                    showCounter && 'flex gap-3 items-start'
                )}
            >
                {showCounter && <QuestionCounterLabel index={index} className="mt-0.5" />}
                <div className="min-w-0 flex-1">
                    {item.data.title &&
                    typeof item.data.title === 'object' &&
                    isTiptapDocument(item.data.title) ? (
                        <div className="speaking-player-prompt-heading text-foreground mb-3">
                            <RichTextViewer data={item.data.title} className="max-w-full!" />
                        </div>
                    ) : (
                        <h4 className="speaking-player-prompt-heading text-base font-semibold text-foreground mb-3">
                            {(typeof item.data.title === 'string' && item.data.title.trim()) ||
                                'Speaking task'}
                        </h4>
                    )}
                    <RecordAudioPlayer
                        data={item.data}
                        answer={answers[item.id]}
                        onAnswer={(v) => handlePromptAnswer(item.id, v)}
                        disabled={disabled}
                        isReview={isReview}
                        hideTitle
                    />
                </div>
            </div>
        </div>
    );

    let promptsBody: React.ReactNode = null;
    if (items.length > 0) {
        if (displayMode === 'all' || submittedOrFeedback) {
            promptsBody = (
                <CompositePlayerGroupedItemsBody
                    prefix="speaking-player"
                    itemIds={itemIds}
                    questionsSidebarRows={safeData.questionsSidebarRows}
                    renderItem={(id) => {
                        const item = itemById.get(id);
                        if (!item) {
                            return null;
                        }
                        const idx = orderedItemIndexById.get(id) ?? 0;
                        return renderPromptCard(item, idx);
                    }}
                />
            );
        } else {
            promptsBody = (
                <div className="speaking-player-prompts-list speaking-player-prompts-list--tabs flex flex-col gap-6">
                    {orderedItems[stepIndex]
                        ? renderPromptCard(orderedItems[stepIndex], stepIndex)
                        : null}
                    {orderedItems.length > 1 ? (
                        <div
                            className="speaking-player-tab-row flex flex-wrap justify-center gap-2 border-t border-border pt-4"
                            role="tablist"
                            aria-label={t('speakingPromptsHeading', 'Speaking prompts')}
                        >
                            {map(orderedItems, (item, idx) => {
                                const tabProps = getSpeakingTabButtonProps({
                                    isCurrent: idx === stepIndex,
                                    answered: isPromptRecorded(answers[item.id]),
                                    visited: visitedTabIndices.has(idx),
                                });
                                return (
                                    <Button
                                        key={item.id}
                                        type="button"
                                        size="sm"
                                        variant={tabProps.variant}
                                        className={tabProps.className}
                                        aria-selected={idx === stepIndex}
                                        onClick={() => setStepIndex(idx)}
                                        disabled={disabled}
                                    >
                                        {idx + 1}
                                    </Button>
                                );
                            })}
                        </div>
                    ) : null}
                </div>
            );
        }
    }

    const promptsHeading = (
        <h3 className="speaking-player-prompts-heading text-lg font-semibold text-foreground flex items-center gap-2">
            <Volume2 className="h-5 w-5 text-primary shrink-0" aria-hidden />
            {t('speakingPromptsHeading', 'Speaking prompts')}
        </h3>
    );

    const promptsScrollable = (
        <>
            {items.length === 0 ? (
                <p className="speaking-player-empty text-sm text-muted-foreground">
                    {t('noQuestions', 'No prompts configured.')}
                </p>
            ) : null}
            {promptsBody}
        </>
    );

    const promptsColumn = (
        <div
            className={cn(
                'speaking-player-prompts-column min-w-0 flex flex-col gap-4',
                panelHorizontal && 'min-h-0 flex-1'
            )}
        >
            {promptsHeading}
            {panelHorizontal ? (
                <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain border-l border-border pl-4 lg:pl-6">
                    <div className="space-y-6 pr-1">{promptsScrollable}</div>
                </div>
            ) : (
                <div className="space-y-6">{promptsScrollable}</div>
            )}
        </div>
    );

    const pageBlock = (
        // EAA-2063: consistent inner padding around the page content (it sat flush against
        // the card edges).
        <div className="speaking-player-page p-4">
            <PagePlayer data={pageContent} className="speaking-player-page-inner" />
        </div>
    );

    let mainBody: React.ReactNode;
    if (panelHorizontal) {
        mainBody = (
            <ContentPlayerSplitLayout
                prefix="speaking-player-split"
                splitRatio={splitRatio}
                leftPanel={
                    // EAA-2063: the page is one card in the split layout too (same shell as
                    // the stacked layout), instead of its blocks sitting on the page background.
                    // EAA-2063: the picture column may use more of the screen height.
                    <div
                        className="speaking-player-split-page min-w-0 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain pr-1"
                        style={imageMaxHeightVars(SPLIT_COLUMN_IMAGE_MAX_HEIGHT)}
                    >
                        <div className="speaking-player-page-shell rounded-xl border-0 shadow-sm bg-card">
                            {pageBlock}
                        </div>
                    </div>
                }
                rightPanel={
                    <div className="speaking-player-split-prompts min-w-0 min-h-0 flex flex-col overflow-hidden">
                        {promptsColumn}
                    </div>
                }
            />
        );
    } else {
        mainBody = (
            <>
                {hasPageContent ? (
                    <div className="speaking-player-page-shell rounded-xl border-0 shadow-sm overflow-hidden bg-card">
                        {pageBlock}
                    </div>
                ) : null}
                {promptsColumn}
            </>
        );
    }

    return (
        <div
            className={cn(
                'speaking-player',
                panelHorizontal
                    ? 'speaking-player--horizontal-split flex flex-col min-h-0 flex-1 gap-6'
                    : 'flex flex-col gap-6',
                className
            )}
        >
            {hasRootDescription && (
                <div className="speaking-player-header speaking-player-header--intro flex-shrink-0 space-y-2">
                    <p className="speaking-player-description text-sm text-muted-foreground leading-relaxed">
                        {safeData.description}
                    </p>
                </div>
            )}

            {mainBody}

            {/* EAA-1890: Read Aloud comes back from SpeechAce with per-word quality, so it
                gets the interactive-reading treatment (coloured passage + score card);
                every other QT_SPEAK_* type returns criteria prose and keeps the AI panel.
                Both guard on their own `details` flag, so only one ever renders. */}
            {/* ELMS-2370: hide answer => no pronunciation / AI score panels. */}
            {submittedOrFeedback && revealCorrect && result?.details ? (
                <>
                    <SpeakingPronunciationPanel
                        details={result.details as Record<string, unknown>}
                        items={orderedItems}
                        passingThreshold={config?.scoring?.passingThreshold}
                        t={t}
                    />
                    <SpeakingAiGradePanel
                        details={result.details as Record<string, unknown>}
                        items={orderedItems}
                        t={t}
                    />
                </>
            ) : null}

            {/* EAA-1873: Speaking is a non-media interaction, so its authored or
            generated rationale belongs in post-submit feedback. */}
            {submittedOrFeedback && revealCorrect && safeData.explanation?.trim() ? (
                <div className="speaking-player-explanation">
                    <FeedbackCard type="explanation" title={t('explanation', 'Explanation')}>
                        {safeData.explanation}
                    </FeedbackCard>
                </div>
            ) : null}
        </div>
    );
};

export default SpeakingPlayer;
